import { TestKitType, UserType } from "@/common/types";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { Input } from "@/components/ui/input";
import { SendHorizontal } from "lucide-react";
import { convertDateToString } from "@/utils/date.util";
import AssignToDoctor from "@/containers/test-detail/workflows/assign-to-doctor";
import AssignToPatient from "@/containers/test-detail/workflows/assign-to-patient";
import CollectSample from "@/containers/test-detail/workflows/collect-sample";
import MarkInProgress from "@/containers/test-detail/workflows/mark-in-progress";
import UploadCsv from "@/containers/test-detail/workflows/upload-csv";
import UploadPdf from "@/containers/test-detail/workflows/upload-pdf";
import ShareResultToPatient from "@/containers/test-detail/workflows/share-result-to-patient";
import { useDispatch, useSelector } from "react-redux";
import {
  downloadAnalysis,
  downloadResult,
  getTestKitDetail,
  updateTestKitNotes,
} from "@/redux/actions/testKitActions";
import { RootState } from "@/redux/reducers";
import {
  isUserAdmin,
  isUserDoctor,
  isUserPatient,
  transformKitStatus,
} from "@/utils/common.util";
import TestDetailMoreInfo from "@/containers/test-detail/more-info";
import ConsentForm from "./consent-form";
import RevokeResultFromPatient from "./workflows/revoke-result-from-patient";

const TestDetail = () => {
  const [testDetail, setTestDetail] = useState<TestKitType>({} as TestKitType);
  const [formState, setFormState] = useState({
    note: "",
  });
  const [error, setError] = useState<string>("");

  const [showConsentForm, setShowConsentForm] = useState<boolean>(false);

  const { testKitDetail } = useSelector((state: RootState) => state.testKit);

  const params = useParams();
  const dispatch: any = useDispatch();

  const handleChange = (key: string, value: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const validateInput = () => {
    let errorMessage = "";
    if (!formState.note || formState.note === "") {
      errorMessage = "Enter a note";
    } else {
      errorMessage = "";
    }
    setError(errorMessage);
    return errorMessage;
  };

  const handleSendNotes = () => {
    const errorMessage = validateInput();
    if (!errorMessage) {
      const payload = {
        note: formState.note,
      };
      dispatch(
        updateTestKitNotes(Number(params.id), payload, () =>
          setFormState({ note: "" })
        )
      );
    }
  };

  const handleDownloadAnalysis = () => {
    if (!testDetail.taxa_csv_url || !testDetail.metrics_csv_url) {
      return;
    }
    dispatch(downloadAnalysis(Number(params.id)));
  };

  const handleDownloadResult = () => {
    if (!testDetail.results_url) {
      return;
    }
    dispatch(downloadResult(Number(params.id)));
  };

  useEffect(() => {
    if (testKitDetail) {
      setTestDetail(testKitDetail as TestKitType);
    }
  }, [testKitDetail]);

  useEffect(() => {
    if (params.id !== undefined) {
      dispatch(getTestKitDetail(Number(params.id)));
    }
  }, [params.id]);

  const renderItem = (label: string, value: string | undefined) => {
    return (
      <div className="flex items-center justify-between">
        <p className="font-bold">{label}</p>
        <p className="text-gray-500 font-normal text-sm">{value}</p>
      </div>
    );
  };

  const renderDoctorOrPatient = (
    type: string,
    userDetails: UserType | null
  ) => {
    return (
      <div className="flex items-start justify-between gap-2">
        <p className="font-bold">{type}</p>
        {userDetails ? (
          <p className="text-gray-500 font-normal text-sm flex flex-col items-end">
            <span>{userDetails?.name}</span>
            {!isUserPatient() && (
              <span className="break-all">{userDetails?.email}</span>
            )}
          </p>
        ) : (
          <p className="text-gray-500 font-normal text-sm flex flex-col items-end">
            -
          </p>
        )}
      </div>
    );
  };

  const renderNotes = () => {
    return (
      !isUserPatient() && (
        <div className="col-span-4 md:col-span-1 border-l border-transparent md:border-l md:border-gray-200 p-4 h-auto md:h-[calc(100vh-146px)]">
          <p className="text-2xl font-bold mb-5">Notes</p>
          <ScrollArea
            className={`${
              error
                ? "h-[150px] md:h-[calc(100vh-296px)]"
                : "h-[150px] md:h-[calc(100vh-268px)]"
            } overflow-auto`}
          >
            {testDetail?.notes?.split("\n").map((note, index) => (
              <p
                key={index}
                className="text-gray-500 text-wrap text-sm break-all font-normal mb-2"
              >
                {note}
              </p>
            ))}
          </ScrollArea>
          {error && <p className="text-red-500 text-sm py-1">{error}</p>}
          <div className="flex items-center gap-2">
            <Input
              id="notes"
              name="notes"
              value={formState.note}
              onChange={(e) => handleChange("note", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSendNotes()}
            />
            <SendHorizontal
              className="w-6 h-6 cursor-pointer"
              onClick={() => handleSendNotes()}
            />
          </div>
        </div>
      )
    );
  };

  return (
    testDetail && (
      <div className="p-4 md:p-8 md:pb-0 md:pt-4">
        <div className="pb-4 flex items-center justify-end h-auto md:h-[56px] gap-2 flex-wrap">
          {isUserAdmin() && testDetail.status === "UNASSIGNED" && (
            <AssignToDoctor />
          )}
          {isUserDoctor() && testDetail.status === "ASSIGNED_TO_DOCTOR" && (
            <AssignToPatient />
          )}
          {isUserDoctor() && testDetail.status === "UNASSIGNED" && (
            <AssignToPatient />
          )}
          {isUserDoctor() && testDetail.status === "AWAITING_SAMPLE" && (
            <CollectSample />
          )}
          {isUserAdmin() && testDetail.status === "SAMPLE_COLLECTED" && (
            <MarkInProgress />
          )}
          {isUserAdmin() && testDetail.status === "IN_PROGRESS" && (
            <UploadCsv />
          )}
          {isUserAdmin() && testDetail.status === "ANALYSIS_READY" && (
            <UploadPdf />
          )}
          {isUserAdmin() && testDetail.status === "ANALYSIS_READY" && (
            <UploadCsv />
          )}
          {(isUserAdmin() || isUserDoctor()) &&
            testDetail.status === "REPORT_READY" &&
            !testDetail.results_shared_with_patient && <ShareResultToPatient />}
          {(isUserAdmin() || isUserDoctor()) &&
            testDetail.status === "REPORT_READY" &&
            testDetail.results_shared_with_patient && (
              <RevokeResultFromPatient />
            )}
          {isUserAdmin() && testDetail.status === "REPORT_READY" && (
            <UploadPdf />
          )}
          {isUserAdmin() && testDetail.status === "REPORT_READY" && (
            <UploadCsv />
          )}
        </div>
        <div className="grid grid-cols-4 gap-2 w-full">
          <div className="col-span-4 md:col-span-1 space-y-3 border-r border-transparent md:border-r md:border-gray-200 p-4 h-auto md:h-[calc(100vh-146px)]">
            <p className="text-2xl font-bold mb-5">Test Kit Details</p>
            {renderItem("Kit ID", testDetail.kit_id)}
            {renderItem("Status", transformKitStatus(testDetail.status))}
            {!isUserDoctor() &&
              renderDoctorOrPatient("Doctor", testDetail.assigned_doctor)}
            {!isUserPatient() &&
              renderDoctorOrPatient("Patient", testDetail.assigned_patient)}
            {!isUserPatient() &&
              renderItem(
                "Results shared with patient",
                testDetail.results_shared_with_patient ? "Yes" : "No"
              )}
            {isUserAdmin() &&
              testDetail.taxa_csv_url &&
              testDetail.metrics_csv_url && (
                <div className="flex items-center justify-between">
                  <p className="font-bold">Test Analysis</p>
                  <div
                    onClick={() => handleDownloadAnalysis()}
                    className="text-gray-500 text-wrap text-sm break-all font-normal cursor-pointer hover:text-blue-500"
                  >
                    Download Test Analysis
                  </div>
                </div>
              )}
            {!testDetail.results_url && renderItem("Results", "-")}
            {testDetail.results_url && (
              <div className="flex items-center justify-between">
                <p className="font-bold">Report</p>
                <div
                  onClick={() => handleDownloadResult()}
                  className="text-gray-500 text-wrap text-sm break-all font-normal cursor-pointer hover:text-blue-500"
                >
                  {testDetail.results_url ? "View/Download Report" : "-"}
                </div>
              </div>
            )}
            {isUserAdmin() &&
              renderItem(
                "Created At",
                convertDateToString(testDetail.created_at) || "-"
              )}
            {isUserAdmin() &&
              renderItem(
                "Updated At",
                convertDateToString(testDetail.updated_at) || "-"
              )}
            {/* Questionnaire Section */}
            {(testDetail.assigned_patient != null || isUserPatient()) && (
              <div className="flex items-center justify-between">
                <p className="font-bold">Questionnaire</p>
                {!testDetail.is_patient_questionnaire_filled &&
                  (isUserPatient() ? (
                    <div
                      onClick={() => window.open(testDetail.questionnaire_url)}
                      className="text-red-500 text-wrap text-sm break-all font-bold cursor-pointer hover:text-blue-500"
                    >
                      Complete Now
                    </div>
                  ) : (
                    <p className="text-gray-500 font-normal text-sm">Pending</p>
                  ))}
                {testDetail.is_patient_questionnaire_filled && (
                  <p className="text-gray-500 font-normal text-sm">Completed</p>
                )}
              </div>
            )}
            {/* Consent Form Section */}
            {(testDetail.assigned_patient != null || isUserPatient()) && (
              <div className="flex items-center justify-between">
                <p className="font-bold">Consent Form</p>
                {!testDetail.is_patient_consent_given &&
                  (isUserPatient() ? (
                    <div
                      onClick={() => setShowConsentForm(true)}
                      className="text-red-500 text-wrap text-sm break-all font-bold cursor-pointer hover:text-blue-500"
                    >
                      Agree Now
                    </div>
                  ) : (
                    <p className="text-gray-500 font-normal text-sm">Pending</p>
                  ))}
                {testDetail.is_patient_consent_given && (
                  <p className="text-gray-500 font-normal text-sm">Agreed</p>
                )}
              </div>
            )}
          </div>

          <div className="col-span-4 md:col-span-2">
            <TestDetailMoreInfo
              testId={testDetail.id}
              patientId={testDetail.assigned_patient?.id}
              testStatus={testDetail.status}
              questionnaireStatus={testDetail.is_patient_questionnaire_filled}
              logs={testDetail.log}
            />
          </div>

          {renderNotes()}
        </div>

        {showConsentForm && (
          <ConsentForm
            isOpen={showConsentForm}
            setIsOpen={setShowConsentForm}
            kitId={testDetail.kit_id}
            testId={testDetail.id}
          />
        )}
      </div>
    )
  );
};

export default TestDetail;
